import React, { useMemo } from "react";

import { useGetFarms, useNavigate, useTranslation } from "utils";

import { COVER_CROP_MIXER_ROUTE, TERRA_ROUTE, soilSightRoute } from "consts";

import { Icon, Typography } from "components";

import { AgronomistToolCard, AgronomistToolCardProps } from "pages/app/home/agronomist/AgronomistToolCard";
import { TerraLauncher } from "pages/app/home/agronomist/TerraLauncher";
import { AgronomistCardId, agronomistCards, programHidesAgronomist } from "pages/app/home/agronomist/agronomistCards";
import { pickRecommendedCard } from "pages/app/home/agronomist/recommendedCard";
import { useAgronomistSignals } from "pages/app/home/agronomist/useAgronomistSignals";

/** the sub-headline counts the cards in words */
const subHeadlineKeys = (cardCount: number) => {
  switch (cardCount) {
    case 1:
      return { farm: "agronomist.subHeadline.one", noFarm: "agronomist.subHeadlineNoFarm.one" };
    case 2:
      return { farm: "agronomist.subHeadline.two", noFarm: "agronomist.subHeadlineNoFarm.two" };
    default:
      return { farm: "agronomist.subHeadline.three", noFarm: "agronomist.subHeadlineNoFarm.three" };
  }
};

/** The SoilSight pages read the entry point for their analytics; the card keeps the value of the home card it replaces, so the funnel stays comparable. */
const FROM_HOME_CARD = { state: { entryPoint: "home_promo" } };

type CardContent = Omit<AgronomistToolCardProps, "id" | "recommended">;

type AgronomistSectionProps = {
  /** from their flag gates, which keep their answer while FeatBit identifies the user again after a farm switch */
  soilSightEnabled: boolean;
  copilotEnabled: boolean;
};

/** The digital agronomist band of the home page: the tools available to the farm, one of them marked as the place to start until the farm has used it. */
export const AgronomistSection = ({ soilSightEnabled, copilotEnabled }: AgronomistSectionProps) => {
  const { t } = useTranslation(["home", "nav"]);
  const navigate = useNavigate();
  const { currentFarm, isLoading } = useGetFarms();

  const carbonProgram = currentFarm?.carbonProgram;
  const cards = useMemo(() => agronomistCards({ carbonProgram, soilSightEnabled, copilotEnabled }), [carbonProgram, soilSightEnabled, copilotEnabled]);
  // nothing is requested for a section that will not render: farms still loading, no farm, a program without the agronomist
  const hidden = isLoading || !currentFarm || programHidesAgronomist(carbonProgram) || cards.length === 0;
  const signals = useAgronomistSignals({
    loadScenarios: !hidden && cards.includes("soilSight"),
    loadMixes: !hidden && cards.includes("coverCropMixer"),
  });

  const recommendedCard = pickRecommendedCard(cards, signals);

  if (hidden) return null;

  const cardContent = (id: AgronomistCardId): CardContent => {
    switch (id) {
      case "soilSight":
        return {
          icon: "klim/crop-rotation",
          name: t("agronomist.soilSight.name"),
          title: t("agronomist.soilSight.title"),
          hook: t("agronomist.soilSight.hook"),
          cta: { label: t("agronomist.soilSight.cta"), onClick: () => navigate(soilSightRoute(signals.hasScenarios), FROM_HOME_CARD) },
        };
      case "coverCropMixer":
        return {
          icon: "klim/plant-ai",
          name: t("agronomist.coverCropMixer.name"),
          title: t("agronomist.coverCropMixer.title"),
          hook: t("agronomist.coverCropMixer.hook"),
          cta: { label: t("agronomist.coverCropMixer.cta"), onClick: () => navigate(COVER_CROP_MIXER_ROUTE) },
        };
      case "terra":
        return {
          icon: "klim/magic",
          name: t("agronomist.terra.name"),
          title: t("agronomist.terra.title"),
          hook: t("agronomist.terra.hook"),
          tag: t("nav:beta").toUpperCase(),
          cta: { label: t("agronomist.terra.cta"), onClick: () => navigate(TERRA_ROUTE) },
        };
      default: {
        const unknownCard: never = id;
        return unknownCard;
      }
    }
  };

  const { farm: subHeadlineKey, noFarm: subHeadlineNoFarmKey } = subHeadlineKeys(cards.length);
  const subHeadline = currentFarm.name ? t(subHeadlineKey, { farmName: currentFarm.name }) : t(subHeadlineNoFarmKey);

  return (
    <>
      <section className="agronomist-section" data-testid="agronomist-section" aria-labelledby="agronomist-section-headline">
        <div className="agronomist-section-header">
          <span className="agronomist-section-eyebrow">
            <Icon iconName="klim/support-chat" size={14} />
            {t("agronomist.eyebrow")}
          </span>
          <Typography variant="h2" className="agronomist-section-headline" id="agronomist-section-headline" role="heading" aria-level={2}>
            {t("agronomist.headline")}
          </Typography>
          <Typography variant="paragraph-m" className="agronomist-section-sub-headline" data-testid="agronomist-section-sub-headline">
            {subHeadline}
          </Typography>
        </div>
        <div className="agronomist-section-cards">
          {cards.map((id) => (
            <AgronomistToolCard key={id} id={id} recommended={recommendedCard === id} {...cardContent(id)} />
          ))}
        </div>
      </section>
      {cards.includes("terra") ? <TerraLauncher /> : null}
    </>
  );
};
